import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Check, Tag, X, ExternalLink, Play } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { AuthContext } from '../App';
import { ALL_PLANS } from '../config/plans';
import { usePlanPrices, applyLivePrices } from '../utils/livePrices';
import { getDeviceFingerprint } from '../utils/deviceFingerprint';
import { isMobileApp, openWebappWithAuth } from '../utils/platform';
import SEOHead from './SEOHead';

const API = process.env.REACT_APP_BACKEND_URL || '';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { planId } = useParams();
  const { user, openAuth } = useContext(AuthContext);
  const [plan, setPlan] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [offerZone, setOfferZone] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const livePrices = usePlanPrices();

  useEffect(() => {
    // Get plan from URL parameter - allow guests to view checkout
    if (planId) {
      const foundPlan = applyLivePrices(ALL_PLANS, livePrices).find(p => p.id === planId);
      if (foundPlan) {
        setPlan(foundPlan);
        // Fetch offer zone content for this specific plan
        fetchOfferZone(foundPlan.id);
        
        // Check if promo code was passed via location.state
        if (location.state?.promoCode && location.state?.discountPercentage) {
          setCouponCode(location.state.promoCode);
          setAppliedCoupon({
            code: location.state.promoCode,
            discount_percentage: location.state.discountPercentage
          });
          toast.success(`${location.state.discountPercentage}% discount applied!`);
        }
      } else {
        // Invalid plan ID, redirect to pricing
        toast.error('Invalid plan selected');
        navigate('/pricing');
      }
    } else {
      // No plan ID, redirect back to pricing
      navigate('/pricing');
    }
  }, [planId, navigate, location.state, livePrices]);

  const fetchOfferZone = async (planId) => {
    try {
      const response = await axios.get(`${API}/api/offer-zone/${planId}`);
      console.log('Offer zone response:', response.data);
      if (response.data.success && response.data.offer_zone) {
        console.log('Offer zone hyperlinks:', response.data.offer_zone.hyperlinks);
        setOfferZone(response.data.offer_zone);
      }
    } catch (error) {
      console.error('Failed to fetch offer zone:', error);
    }
  };

  const calculatePricing = () => {
    if (!plan) return { base: 0, gst: 0, discount: 0, total: 0 };

    const basePrice = plan.price;
    
    // Step 1: Apply discount on the fee first
    let discount = 0;
    let discountedFee = basePrice;
    if (appliedCoupon) {
      discount = basePrice * (appliedCoupon.discount_percentage / 100);
      discountedFee = basePrice - discount; // Fee after discount
    }
    
    // Step 2: Calculate GST on the discounted fee (not original fee)
    const gst = discountedFee * 0.18;
    
    // Step 3: Final total = Discounted Fee + GST on Discounted Fee
    const total = discountedFee + gst;

    return {
      base: basePrice,
      gst: gst.toFixed(2),
      discount: discount.toFixed(2),
      total: total.toFixed(2)
    };
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    setValidatingCoupon(true);
    try {
      // Check if promo code is valid
      const checkResponse = await axios.get(`${API}/api/promo/${couponCode.toUpperCase()}/check`);
      
      if (!checkResponse.data.valid) {
        toast.error(checkResponse.data.message || 'Invalid promo code');
        setValidatingCoupon(false);
        return;
      }

      // Verify it's a discount type promo
      if (checkResponse.data.type !== 'discount') {
        toast.error('This promo code cannot be applied here');
        setValidatingCoupon(false);
        return;
      }

      // Check if applicable to this plan
      const applicablePlans = checkResponse.data.applicable_plans || [];
      if (!applicablePlans.includes('all') && !applicablePlans.includes(plan.id)) {
        toast.error('This promo code is not applicable to this plan');
        setValidatingCoupon(false);
        return;
      }

      // Redeem the promo code if user is logged in
      if (user) {
        const deviceFp = await getDeviceFingerprint();
        await axios.post(`${API}/api/promo/redeem`, {
          promo_code: couponCode.toUpperCase(),
          device_fingerprint: deviceFp
        });
      }

      // Apply the coupon
      setAppliedCoupon({
        code: couponCode.toUpperCase(),
        discount_percentage: checkResponse.data.discount_percentage
      });
      toast.success(`${checkResponse.data.discount_percentage}% discount applied!`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to validate coupon';
      toast.error(errorMsg);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    toast.info('Coupon removed');
  };

  const initiatePayment = async () => {
    // On mobile app: open website in browser with auto-login instead of Razorpay inside app
    if (isMobileApp()) {
      toast.info('Opening website to complete payment...');
      await openWebappWithAuth(`/checkout/${planId || plan?.id}`);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login to continue');
      navigate('/pricing');
      return;
    }

    setLoading(true);
    try {
      const pricing = calculatePricing();

      // Check if amount is 0 (100% discount) - Direct enrollment without payment gateway
      if (parseFloat(pricing.total) === 0) {
        try {
          // Directly call verify endpoint with free enrollment flag
          const verifyResponse = await axios.post(
            `${API}/api/payments/verify-free-enrollment`,
            {
              plan_id: plan.id,
              coupon_code: appliedCoupon?.code || null
            },
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );

          if (verifyResponse.data.success) {
            toast.success('Enrolled successfully with 100% discount!');
            const successUrl = `/payment-success?plan=${encodeURIComponent(plan.name)}&duration=${encodeURIComponent(plan.duration)}&free=true`;
            window.location.href = successUrl;
          }
        } catch (error) {
          console.error('Free enrollment failed:', error);
          toast.error(error.response?.data?.detail || 'Enrollment failed');
          setLoading(false);
        }
        return;
      }

      // Create order
      const orderResponse = await axios.post(
        `${API}/api/payments/create-order`,
        {
          amount: parseInt(pricing.total),
          base_amount: plan.price,
          currency: 'INR',
          plan_id: plan.id,
          discount_amount: parseFloat(pricing.discount) || 0,
          discount_percentage: appliedCoupon?.discount_percentage || 0,
          coupon_code: appliedCoupon?.code || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const { order_id, amount: orderAmount, currency, key_id } = orderResponse.data;

      // Load Razorpay script
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;

      script.onload = () => {
        setLoading(false);

        const options = {
          key: key_id,
          amount: orderAmount,
          currency: currency,
          name: 'Nerd AI',
          description: plan.name,
          order_id: order_id,
          handler: async (response) => {
            try {
              setLoading(true);
              const verifyResponse = await axios.post(
                `${API}/api/payments/verify`,
                {
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature
                },
                {
                  headers: { Authorization: `Bearer ${token}` }
                }
              );

              if (verifyResponse.data.success) {
                toast.success('Payment successful!');
                const successUrl = `/payment-success?plan=${encodeURIComponent(plan.name)}&duration=${encodeURIComponent(plan.duration)}&order_id=${order_id}`;
                window.location.href = successUrl;
              }
            } catch (error) {
              console.error('Payment verification failed:', error);
              toast.error('Payment verification failed');
              setLoading(false);
            }
          },
          modal: {
            ondismiss: function () {
              setLoading(false);
              toast.info('Payment cancelled');
            }
          },
          theme: {
            color: '#9333ea'
          }
        };

        const razorpay = new window.Razorpay(options);
        razorpay.open();
      };

      script.onerror = (error) => {
        console.error('Failed to load Razorpay script', error);
        toast.error('Failed to load payment gateway');
        setLoading(false);
      };

      document.body.appendChild(script);
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  if (!plan) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading checkout...</p>
        </div>
      </div>
    );
  }

  const pricing = calculatePricing();

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30 py-8 px-4">
      <SEOHead title={plan ? `Checkout — ${plan.name}` : 'Checkout'} description={plan ? `Subscribe to ${plan.name} on Nerd Nation.` : 'Complete your subscription.'} path={`/checkout/${planId}`} noIndex={true} />
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/pricing')}
            className="mb-4"
          >
            ← Back to Pricing
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Checkout</h1>
          <p className="text-gray-600 mt-2">Review your order and complete payment</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Section - Plan Details & Offer Zone */}
          <div className="lg:col-span-2 space-y-6">
            {/* Plan Details */}
            <Card>
              <CardHeader>
                <CardTitle>Plan Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{plan.icon}</span>
                        <h3 className="text-xl font-semibold">{plan.name}</h3>
                      </div>
                      {plan.subtitle && (
                        <p className="text-sm text-gray-600 mt-1">{plan.subtitle}</p>
                      )}
                      <p className="text-sm text-indigo-600 font-medium mt-2">
                        Duration: {plan.duration}
                      </p>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-3">Features Included:</h4>
                    <div className="grid gap-2">
                      {plan.features?.map((feature, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Offer Zone */}
            {offerZone && (
              <Card className="border-2 border-orange-300 bg-gradient-to-r from-orange-50 to-yellow-50">
                <CardHeader>
                  <CardTitle className="text-orange-700">🎁 Special Offer</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Content */}
                  {offerZone.content && (
                    <div 
                      className="text-sm text-gray-700 whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{ __html: offerZone.content }}
                    />
                  )}

                  {/* Video */}
                  {offerZone.video_url && (
                    <div className="rounded-lg overflow-hidden">
                      <iframe
                        width="100%"
                        height="300"
                        src={offerZone.video_url.replace('watch?v=', 'embed/')}
                        title="Promotional Video"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="rounded-lg"
                      ></iframe>
                    </div>
                  )}

                  {/* Images */}
                  {offerZone.images && offerZone.images.length > 0 && (
                    <div className="grid grid-cols-2 gap-4">
                      {offerZone.images.map((img, index) => (
                        <img
                          key={index}
                          src={img}
                          alt={`Offer ${index + 1}`}
                          className="rounded-lg w-full h-40 object-cover" loading="lazy" decoding="async"/>
                      ))}
                    </div>
                  )}

                  {/* Hyperlinks */}
                  {offerZone.hyperlinks && offerZone.hyperlinks.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Useful Links:</p>
                      {offerZone.hyperlinks.map((link, index) => (
                        <a
                          key={index}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 text-base font-medium underline"
                        >
                          <ExternalLink className="h-5 w-5" />
                          {link.text}
                        </a>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Section - Payment Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Payment Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Coupon Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Have a coupon code?
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        placeholder="Enter code"
                        className="pl-10"
                        disabled={!!appliedCoupon}
                      />
                    </div>
                    {!appliedCoupon ? (
                      <Button
                        onClick={handleApplyCoupon}
                        disabled={validatingCoupon || !couponCode.trim()}
                        size="sm"
                      >
                        {validatingCoupon ? 'Checking...' : 'Apply'}
                      </Button>
                    ) : (
                      <Button
                        onClick={handleRemoveCoupon}
                        variant="outline"
                        size="sm"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {appliedCoupon && (
                    <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                      <Check className="h-4 w-4" />
                      {appliedCoupon.discount_percentage}% discount applied!
                    </p>
                  )}
                </div>

                {/* Price Breakdown */}
                <div className="border-t pt-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Base Price</span>
                    <span className="font-medium">₹{pricing.base}</span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">GST (18%)</span>
                    <span className="font-medium">₹{pricing.gst}</span>
                  </div>

                  {appliedCoupon && parseFloat(pricing.discount) > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Discount ({appliedCoupon.discount_percentage}%)</span>
                      <span className="font-medium">-₹{pricing.discount}</span>
                    </div>
                  )}

                  <div className="border-t pt-3 flex justify-between">
                    <span className="font-semibold text-lg">Total</span>
                    <span className="font-bold text-xl text-indigo-600">
                      ₹{pricing.total}
                    </span>
                  </div>
                </div>

                {/* Register Now / Proceed to Payment Button */}
                <Button
                  onClick={() => {
                    if (!user) {
                      // Guest user - show auth modal for registration
                      openAuth('register');
                    } else {
                      // Logged-in user - proceed to payment
                      initiatePayment();
                    }
                  }}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold py-6 text-lg"
                >
                  {loading ? 'Processing...' : (user ? 'Proceed to Payment' : 'Register Now')}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  By proceeding, you agree to our Terms of Service and Privacy Policy
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
